import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

// SUPERADMIN-15 (MA-28c) — READ-ONLY feature-flag reference panel.
//
// There is no backend feature-flag/config controller today (grep-confirmed: no FlagController,
// no GET /admin/flags). Per the felie brief, reading live values via actuator/env is NOT an
// acceptable substitute, so this page renders the KNOWN kill-switch registry as a static reference
// (source of truth: docs/ops/OPERATOR_HANDBOOK.md §1) with an explicit honest note that current
// values require a backend endpoint. It is deliberately read-only — the write/flip path is
// OWNER-GATE #10 (money/fiscal/legal blast radius) and is intentionally NOT built here.

type Risk = 'HIGH' | 'MEDIUM' | 'LOW';

interface FlagRow {
  key: string;
  category: string;
  defaultState: string;
  effect: string;
  risk: Risk;
  ownerGated: boolean;
}

// Curated from OPERATOR_HANDBOOK §1 (the flip-checklist). Defaults reflect the documented
// dormant-by-design posture; "effect" describes what turning the flag ON does.
const FLAG_REGISTRY: FlagRow[] = [
  // ---- Fiscal (highest blast radius; every flip owner/accountant-gated) ----
  { key: 'fiscal.adapter.enabled', category: 'Fiscal', defaultState: 'OFF (NoOp)', effect: 'Real fiscal receipt via LDREST / Datecs FP-700 hardware', risk: 'HIGH', ownerGated: true },
  { key: 'accounting.ops.anaf.live-enabled', category: 'Fiscal', defaultState: 'OFF', effect: 'LIVE ANAF submission (SAF-T etc.)', risk: 'HIGH', ownerGated: true },
  { key: 'accounting.ops.anaf.mock-enabled', category: 'Fiscal', defaultState: 'ON', effect: 'ANAF mock (keep ON until live sign-off)', risk: 'MEDIUM', ownerGated: true },
  { key: 'app.billing.anaf.efactura.real-upload-enabled', category: 'Fiscal', defaultState: 'OFF', effect: 'e-Factura real HTTP upload (vs stub) — V-06', risk: 'HIGH', ownerGated: true },
  { key: 'app.billing.anaf.efactura.real-status-enabled', category: 'Fiscal', defaultState: 'OFF', effect: 'e-Factura real status polling', risk: 'HIGH', ownerGated: true },
  { key: 'app.billing.anaf.efactura.real-download-enabled', category: 'Fiscal', defaultState: 'OFF', effect: 'e-Factura real download', risk: 'HIGH', ownerGated: true },
  { key: 'app.billing.anaf.oauth.real-client-enabled', category: 'Fiscal', defaultState: 'OFF', effect: 'Real ANAF OAuth client', risk: 'HIGH', ownerGated: true },
  { key: 'accounting.ops.saft.validator.enabled', category: 'Fiscal', defaultState: 'OFF', effect: 'External SAF-T validator binary', risk: 'MEDIUM', ownerGated: true },
  { key: 'accounting.ops.supplier-posting.enabled', category: 'Fiscal', defaultState: 'ON', effect: 'Supplier-invoice posting into the ledger', risk: 'MEDIUM', ownerGated: true },
  // ---- Payments (money movement; owner-gated) ----
  { key: 'stripe.enabled', category: 'Payments', defaultState: 'OFF', effect: 'Master switch for ALL Stripe beans', risk: 'HIGH', ownerGated: true },
  { key: 'payments.live.enabled', category: 'Payments', defaultState: 'OFF', effect: 'Real captures (vs test mode)', risk: 'HIGH', ownerGated: true },
  { key: 'payments.guest.enabled', category: 'Payments', defaultState: 'OFF', effect: 'Guest card payments (QR / pickup) — Grupa-1 kill-switch', risk: 'HIGH', ownerGated: true },
  { key: 'tenants.pickup_enabled', category: 'Payments', defaultState: 'OFF (per-tenant, DB)', effect: 'Public pickup-order path on the tenant site', risk: 'MEDIUM', ownerGated: true },
  { key: 'events.prepay.enabled', category: 'Payments', defaultState: 'OFF', effect: 'Stripe manual-capture deposit (Nightclub/Events)', risk: 'HIGH', ownerGated: true },
  { key: 'events.ticketing.enabled', category: 'Payments', defaultState: 'OFF', effect: 'Stripe Checkout ticket sales (Nightclub/Events)', risk: 'HIGH', ownerGated: true },
  // ---- Inventory / consumption analytics ----
  { key: 'inventory.spa-bom.enabled', category: 'Inventory', defaultState: 'OFF', effect: 'BOM consumption on spa treatment completion', risk: 'MEDIUM', ownerGated: false },
  { key: 'inventory.spa-retail.enabled', category: 'Inventory', defaultState: 'OFF', effect: 'Spa retail sale (blocking stock pre-check)', risk: 'MEDIUM', ownerGated: false },
  { key: 'inventory.fitness-retail.enabled', category: 'Inventory', defaultState: 'OFF', effect: 'Fitness pro-shop + kiosk retail', risk: 'MEDIUM', ownerGated: false },
  { key: 'inventory.minibar-consume.enabled', category: 'Inventory', defaultState: 'OFF', effect: 'Hotel minibar-as-stock consumption', risk: 'MEDIUM', ownerGated: false },
  { key: 'inventory.amenity-consume.enabled', category: 'Inventory', defaultState: 'OFF', effect: 'Amenity BOM consumption', risk: 'LOW', ownerGated: false },
  { key: 'inventory.housekeeping-consume.enabled', category: 'Inventory', defaultState: 'OFF', effect: 'Housekeeping BOM consumption', risk: 'LOW', ownerGated: false },
  // ---- Vertical master switches ----
  { key: 'medtrace.enabled', category: 'Verticals', defaultState: 'OFF', effect: 'Master switch — OFF returns 404 for all /medtrace', risk: 'MEDIUM', ownerGated: true },
  { key: 'events.core.enabled', category: 'Verticals', defaultState: 'OFF', effect: 'Events core surface', risk: 'MEDIUM', ownerGated: false },
  { key: 'events.pii.purgeEnabled', category: 'Verticals', defaultState: 'OFF', effect: 'Events guest-PII purge job', risk: 'MEDIUM', ownerGated: true },
  { key: 'fastfood.counter.enabled', category: 'Verticals', defaultState: 'OFF', effect: 'Fast-food counter mode', risk: 'LOW', ownerGated: false },
  { key: 'fastfood.kiosk.enabled', category: 'Verticals', defaultState: 'OFF', effect: 'Fast-food self-order kiosk', risk: 'LOW', ownerGated: false },
  { key: 'fastfood.pickupBoard.enabled', category: 'Verticals', defaultState: 'OFF', effect: 'Fast-food pickup board', risk: 'LOW', ownerGated: false },
  // ---- Platform / integrations ----
  { key: 'localfirst.*', category: 'Platform', defaultState: 'OFF', effect: 'PowerSync local-first rollout family (LOCAL_FIRST_POS_W12 etc.)', risk: 'MEDIUM', ownerGated: false },
  { key: 'printer.adapter.enabled', category: 'Platform', defaultState: 'OFF', effect: 'Physical printer adapter', risk: 'LOW', ownerGated: false },
  { key: 'media.signed.enabled', category: 'Platform', defaultState: 'OFF', effect: 'Signed-URL media delivery', risk: 'LOW', ownerGated: false },
  { key: 'delivery.hubrise.real-client-enabled', category: 'Platform', defaultState: 'OFF', effect: 'Real Wolt/HubRise delivery client (needs credentials)', risk: 'MEDIUM', ownerGated: true },
  { key: 'notification.email.enabled', category: 'Platform', defaultState: 'OFF', effect: 'Outbound email notifications (needs provider key)', risk: 'LOW', ownerGated: false },
  { key: 'notification.sms.enabled', category: 'Platform', defaultState: 'OFF', effect: 'Outbound SMS notifications (needs provider key)', risk: 'LOW', ownerGated: false },
  { key: 'jwt.legacy.hmac.enabled', category: 'Platform', defaultState: 'ON', effect: 'Legacy HMAC JWT signing (turn OFF only after full RSA rotation)', risk: 'HIGH', ownerGated: true },
  { key: 'demo.* / seed.demo* / dev.seed.enabled', category: 'Platform', defaultState: 'OFF', effect: 'Demo/dev seeding — NEVER enable on prod', risk: 'HIGH', ownerGated: true },
];

const riskColor = (risk: Risk): 'error' | 'warning' | 'default' => {
  if (risk === 'HIGH') return 'error';
  if (risk === 'MEDIUM') return 'warning';
  return 'default';
};

const FeatureFlagsPage: React.FC = () => {
  const [filter, setFilter] = useState('');

  const rows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return FLAG_REGISTRY;
    return FLAG_REGISTRY.filter(
      (row) =>
        row.key.toLowerCase().includes(needle) ||
        row.category.toLowerCase().includes(needle) ||
        row.effect.toLowerCase().includes(needle),
    );
  }, [filter]);

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Superadmin / Platform
        </Typography>
        <Typography variant="h4">Feature Flags</Typography>
        <Typography color="text.secondary">
          Reference registry of the platform kill-switches an operator may need to know about
          (source: OPERATOR_HANDBOOK §1).
        </Typography>
      </Stack>

      <Alert severity="info" variant="outlined">
        <strong>Read-only view.</strong> Live values require a backend flags endpoint that does not
        exist yet — the states shown here are the <em>documented defaults</em>, not a live read. Flipping
        a flag stays <strong>OWNER-GATE #10</strong> (money / fiscal / legal blast radius) and is
        intentionally not wired on this screen.
      </Alert>

      <TextField
        size="small"
        label="Filter flags"
        placeholder="fiscal, payments, inventory…"
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        sx={{ maxWidth: 360 }}
      />

      <Paper variant="outlined">
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Flag key</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Documented default</TableCell>
                <TableCell>Effect when ON</TableCell>
                <TableCell>Risk</TableCell>
                <TableCell>Flip gate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      No flags match &ldquo;{filter}&rdquo;.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.key} hover>
                    <TableCell>
                      <Box component="code" sx={{ fontSize: 13 }}>
                        {row.key}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={row.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.defaultState}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.effect}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={row.risk} size="small" color={riskColor(row.risk)} />
                    </TableCell>
                    <TableCell>
                      {row.ownerGated ? (
                        <Chip label="Owner-gated" size="small" color="error" variant="outlined" />
                      ) : (
                        <Chip label="Autonomous" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="caption" color="text.secondary">
        {rows.length} of {FLAG_REGISTRY.length} flags shown. This list is a curated reference, not an
        exhaustive dump of every @ConditionalOnProperty in the codebase.
      </Typography>
    </Stack>
  );
};

export default FeatureFlagsPage;
